import React, { useState, useEffect, useMemo } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import PropTypes from 'prop-types';
import { useQuery, useApolloClient } from '@apollo/client';
import { Virtuoso } from 'react-virtuoso';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

// Material UI
import GridOnIcon from '@material-ui/icons/GridOn';
import ListIcon from '@material-ui/icons/List';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';

// app: components
import BookShelf from 'components/BookShelf';
import BookGrid from 'components/BookGrid';
import ContentOptions from 'components/ContentOptions';
import CategoryTabs from 'components/CategoryTabs';
import ErrorRouter from 'components/ErrorRouter';
import HeadContent from 'components/HeadContent';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Link from 'components/Link';
import SearchField from 'components/SearchField';
import SortingOptions from 'components/SortingOptions';

// app modules: hooks
import useFeatureToggle from 'hooks/useFeatureToggle';

// app: services
import formatter from 'services/formatter';
import { booksUrl } from 'services/route';
import { isApp } from 'services/environment';
import history from 'services/history';

// app: constants
import { COLOURS } from 'constants/index';

// app: queries
import GetBookCategoryQuery from './GetBookCategoryQuery.gql';
import GetBookCategoriesQuery from './GetBookCategoriesQuery.gql';

// assets
import CssBooks from './Books.scss';

const pageSize = 25;
const skeletonItems = 3;

function Books(props) {
  const {
    category,
    footer,
    initialItem,
    orderBooksBy,
    orderBooksByDirection,
    orderShelvesBy,
    orderShelvesByDirection,
    shelfStates,
    tab,
    searchQuery,
    viewType,
  } = props;

  const initialLimit = initialItem ? Math.ceil(initialItem / pageSize) * pageSize : pageSize;

  useStyles(CssBooks);

  // We need the display type to stay stable whilst viewing a given tab to avoid messy transitions
  const displayType = useSelector(state => state.dimensions.displayType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialDisplayType = useMemo(() => displayType, [tab]);

  const userId = useSelector(state => state.user.id);
  const userLoggedIn = useSelector(state => state.user.logged_in);
  const country = useSelector(state => state.user.geolocation.countryCode);

  const [hasMore, setHasMore] = useState(true);
  const [query, setQuery] = useState(searchQuery);
  const [alacarteEnabled] = useFeatureToggle('alacarte');
  useEffect(
    () => {
      setHasMore(true);
    },
    [tab, orderShelvesBy, orderShelvesByDirection],
  );

  const apolloClient = useApolloClient();
  const { previousData, data, loading, error, fetchMore } = useQuery(GetBookCategoriesQuery, {
    variables: {
      id: tab,
      limit: initialLimit,
      offset: 0,
      orderBy: orderShelvesBy,
      orderByDirection: orderShelvesByDirection,
      version: 2,
      country,
    },
    fetchPolicy: isApp() ? 'cache-and-network' : 'cache-first',
    errorPolicy: 'all',
  });
  const tabCategoryData = data?.Category || previousData?.Category;

  const viewingShelfContents =
    category ||
    (query && tabCategoryData?.search_category ? tabCategoryData.search_category : null);
  const currentUrl = booksUrl({
    tab,
    category,
    orderBy: viewingShelfContents ? orderBooksBy : orderShelvesBy,
    orderByDirection: viewingShelfContents ? orderBooksByDirection : orderShelvesByDirection,
    query,
    viewType,
  });

  // Lookup the sort options for the search category
  const { data: _searchCategory } = useQuery(GetBookCategoryQuery, {
    variables: {
      id: viewingShelfContents,
    },
    fetchPolicy: isApp() ? 'cache-and-network' : 'cache-first',
    errorPolicy: 'all',
    skip: !viewingShelfContents,
  });
  const categoryData = _searchCategory?.Category;

  useEffect(
    () => {
      setQuery(searchQuery);
    },
    [searchQuery, tab],
  );

  useEffect(
    () => () => {
      apolloClient.cache.evict({ fieldName: 'Books', broadcast: false });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const loadMore = () => {
    if (hasMore) {
      fetchMore({ variables: { offset: data?.Category?.children?.length || 0 } }).then(
        ({ data: fetchMoreData }) => {
          if (fetchMoreData?.Category.children.length === 0) {
            setHasMore(false);
          }
        },
      );
    }
  };

  const getMoreLink = child => {
    if (child.href) {
      return child.href;
    }
    return booksUrl({
      tab: tabCategoryData.id,
      category: child.id,
    });
  };

  const handleItemClick = ({ shelfId, scrollPosition, title, itemIndex }) => {
    history.replace({
      state: {
        ...history.location.state,
        [title]: { scrollPosition, itemIndex },
        initialItem: shelfId,
      },
    });
  };

  const handleQueryChange = event => {
    const { value } = event.target;
    setQuery(formatter.removeSmartCharacters(value));
    if (!value) {
      history.replace({ query: null });
    }
  };

  const handleSearchFormSubmit = event => {
    event.preventDefault();
    history.replace(currentUrl);
  };

  const handleViewChange = (event, value) => {
    history.replace(
      booksUrl({
        tab,
        category: !category && query ? 'popular' : category,
        viewType: value,
        orderBy: orderBooksBy,
        orderByDirection: orderBooksByDirection,
        query,
      }),
    );
  };

  if (error) {
    return <ErrorRouter error={error} />;
  }

  if (!loading && !data?.Category) {
    return <ErrorRouter error={{ code: 'NOT_FOUND' }} />;
  }
  const categories =
    tabCategoryData?.children
      .filter(c => !c.hide)
      .filter(c => (c.user_specific && userLoggedIn) || !c.user_specific) || [];

  let sortOptions;
  let selectedSortOption;

  if (viewingShelfContents) {
    sortOptions = categoryData?.sort_options?.map(option => ({
      key: option.order_by || option.category_id,
      label: option.label,
      direction: option.order_by_direction,
      path: booksUrl({
        tab,
        category: option.category_id || category,
        viewType,
        orderBy: option.order_by,
        orderByDirection: option.order_by_direction,
        query,
      }),
    }));
    selectedSortOption = {
      orderBy: orderBooksBy || categoryData?.id,
      orderByDirection: orderBooksByDirection,
    };
  } else {
    sortOptions = tabCategoryData?.sort_options?.map(option => ({
      key: option.order_by,
      label: option.label,
      direction: option.order_by_direction,
      path: booksUrl({
        tab,
        orderBy: option.order_by,
        orderByDirection: option.order_by_direction,
      }),
    }));
    selectedSortOption = {
      orderBy: orderShelvesBy || tabCategoryData?.order_by,
      orderByDirection: orderShelvesByDirection || tabCategoryData?.order_by_direction,
    };
  }

  const renderItem = (index, child) => {
    if (child && (!child.user_specific || userId > 0)) {
      const shelfState = shelfStates[child.title];
      // Need to remove the __typename from the filter
      const filters = [
        ...child.filters?.map(filter => ({
          field: filter.field,
          values:
            child.user_specific && filter.field.startsWith('user.') ? [`${userId}`] : filter.values,
        })),
      ];

      // If this or any categories above it are user_specific then don't server side render.
      // This is to prevent the strange effect where the lower-down shelves render fast but the (user-specific) shelf at the top is slow.
      const ssr = !categories.slice(0, index).some(c => c.user_specific);

      if (!alacarteEnabled && child.id === 'alacarte') {
        return null;
      }
      return (
        <BookShelf
          backText={tabCategoryData?.title === 'Home' ? 'Books' : tabCategoryData?.title}
          featured={child.featured}
          filters={filters}
          headingLink={child.heading_link}
          id={index}
          includeAlacarte={alacarteEnabled && child.include_alacarte}
          initialScrollPos={shelfState?.scrollPosition || 0}
          itemCount={child.item_count}
          itemWidth={initialDisplayType === 'desktop' ? 250 : 200}
          key={child.id}
          moreLink={tabCategoryData ? getMoreLink(child) : null}
          onItemClick={handleItemClick}
          orderBy={child.order_by}
          orderByDirection={child.order_by_direction}
          preloadItems={initialDisplayType === 'mobile' ? 3 : 8}
          query={child.query}
          showItemCountThreshold={initialDisplayType === 'mobile' ? 3 : 5}
          ssr={ssr}
          title={child.title}
          titleAuthors={child.title_authors}
          useArrows={initialDisplayType === 'desktop'}
          userSpecific={child.user_specific}
        />
      );
    }
    return (
      <BookShelf
        key={`skeleton-${index}`}
        itemWidth={initialDisplayType === 'desktop' ? 250 : 200}
        load={false}
        title={child?.title}
      />
    );
  };
  let metaTitle = viewingShelfContents ? categoryData?.title : tabCategoryData?.title;
  if (query) {
    metaTitle = `Search '${query}'`;
  }
  const shareIcon =
    viewingShelfContents && categoryData?.user_specific === false ? (
      <ContentOptions
        className="books__share"
        heading={metaTitle}
        textToShare={`Book shelf: ${metaTitle}`}
        urlToShare={currentUrl}
        subMenu="share"
        showEmbed={false}
      >
        <Icon
          fill={COLOURS.ENERGIZED}
          name="native-share"
          width="20"
          style={{ position: 'relative', top: 1 }}
        />
      </ContentOptions>
    ) : null;

  return (
    <div
      className={classNames('books', {
        'books--shelves': !viewingShelfContents,
        'books--contents': viewingShelfContents,
        'books--searching': query,
      })}
    >
      <HeadContent
        contents={{
          title: `Books - ${metaTitle || 'ckbk'}`,
          url: currentUrl,
        }}
      />
      <CategoryTabs
        ariaLabel="Books categories"
        className="margin-top-20"
        id="books"
        selected={tab}
        urlBuilder={_tab => booksUrl({ tab: _tab.id })}
      />

      <div className="books__controls">
        <div className="books__heading">
          {category ? (
            <div className="books__nowrap">
              <Heading level={1} className="heading heading--size-32">
                {categoryData?.title} {shareIcon}
              </Heading>
            </div>
          ) : null}
          {viewingShelfContents && categoryData?.grid_heading_link ? (
            <p>
              {categoryData.grid_heading_link.href ? (
                <Link to={categoryData.grid_heading_link.href} className="link--underline">
                  {categoryData.grid_heading_link.text}
                </Link>
              ) : (
                categoryData.grid_heading_link.text
              )}
            </p>
          ) : null}
        </div>

        <div className="books__controls-row">
          {category || query ? (
            <ToggleButtonGroup
              value={viewType}
              exclusive
              onChange={handleViewChange}
              aria-label="list or grid view"
              className="margin-right-10"
            >
              <ToggleButton value="grid" aria-label="grid view">
                <GridOnIcon />
              </ToggleButton>
              <ToggleButton value="list" aria-label="list view">
                <ListIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          ) : (
            <div />
          )}
          {sortOptions && sortOptions.length ? (
            <SortingOptions selected={selectedSortOption} options={sortOptions} />
          ) : null}
        </div>
        <div className="books__search-wrapper">
          {/* Don't render the search field too early or the Mui-JSS styles will get out of sync due
           to it using a sequence number in its class name generator for non-Mui* components */}
          {tabCategoryData && tabCategoryData.search_category ? (
            <form onSubmit={handleSearchFormSubmit} className="books__search-form">
              <SearchField
                label="Search books"
                onChange={handleQueryChange}
                className="books__searchbox"
                inputProps={{
                  autoCapitalize: 'off',
                  autoCorrect: 'off',
                  autoComplete: 'off',
                }}
                value={query}
                onSubmit={handleSearchFormSubmit}
              />
            </form>
          ) : null}
        </div>
      </div>
      <div className="books__body">
        {viewingShelfContents ? (
          <BookGrid
            category={category || tabCategoryData?.search_category}
            footer={footer}
            orderBy={orderBooksBy}
            orderByDirection={orderBooksByDirection}
            searchQuery={query}
            tab={tab || 'home'}
            viewType={viewType}
          />
        ) : (
          <Virtuoso
            useWindowScroll
            style={{ minHeight: '100vh' }}
            data={categories}
            totalCount={categories.length}
            itemContent={renderItem}
            overscan={700}
            // Use for server-side rendering - if set, the list will render the specified amount of items regardless of the container / item size.
            initialItemCount={4}
            initialTopMostItemIndex={Math.max(0, initialItem - 1)}
            endReached={loadMore}
            components={{
              Footer: () => (
                <React.Fragment>
                  {Array(loading ? skeletonItems : 0)
                    .fill()
                    .map((_, i) => renderItem(i, null))}
                  {footer}
                </React.Fragment>
              ),
            }}
          />
        )}
      </div>
    </div>
  );
}

Books.propTypes = {
  category: PropTypes.string,
  footer: PropTypes.node,
  initialItem: PropTypes.number,
  orderBooksBy: PropTypes.string,
  orderBooksByDirection: PropTypes.string,
  orderShelvesBy: PropTypes.string,
  orderShelvesByDirection: PropTypes.string,
  searchQuery: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  shelfStates: PropTypes.object,
  tab: PropTypes.string,
  viewType: PropTypes.oneOf(['grid', 'list']),
};

Books.defaultProps = {
  category: null,
  footer: null,
  initialItem: 0,
  orderBooksBy: null,
  orderBooksByDirection: null,
  orderShelvesBy: null,
  orderShelvesByDirection: null,
  searchQuery: '',
  shelfStates: {},
  tab: 'recommended',
  viewType: 'grid',
};

export default Books;
